import type { BankAccount } from '../types/bank-account'
import type { ConnectivityValue } from '../lib/account-connectivity'
import { WizardCreateAccountFields } from './wizard-create-account-fields'
import { WizardEditAccountFields } from './wizard-edit-account-fields'

interface WizardAccountFormProps {
  connectivity: ConnectivityValue
  // Absent in edit mode: the country only scopes the create-mode institution dropdown.
  countryCode?: string
  editingAccount: BankAccount | null
  onDone: () => void
  onBusyChange: (busy: boolean) => void
}

/**
 * One shared form for every non-Open-Banking connectivity (Manual, Swift, API, SFTP,
 * Email) — no per-type branching on connectivity itself. The institution is picked
 * from a searchable dropdown inside the form, scoped to the wizard's selected
 * country (plus country-less institutions); selecting one autofills
 * the still-editable BIC. Edit mode reuses the existing update mutation/fields
 * instead, since the institution is only chosen at creation time; it saves the
 * selected connectivity when the account may change it (UC-094).
 */
export function WizardAccountForm({
  connectivity, countryCode, editingAccount, onDone, onBusyChange,
}: WizardAccountFormProps) {
  if (editingAccount) {
    return (
      <WizardEditAccountFields
        account={editingAccount}
        connectivity={connectivity}
        onDone={onDone}
        onBusyChange={onBusyChange}
      />
    )
  }

  // Create mode always arrives here from the country step, so a country code is
  // guaranteed; the guard is only what narrows the edit-mode-optional prop.
  if (!countryCode) return null

  return (
    <WizardCreateAccountFields
      connectivity={connectivity}
      countryCode={countryCode}
      onDone={onDone}
      onBusyChange={onBusyChange}
    />
  )
}
